"use client";

import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useAuth } from "@/providers/authProvider";
import { getContentSuggestions } from "@/services/contentSuggestionService";

export function useContentSuggestions(
  slug,
  { enabled = true, limit = 20 } = {}
) {
  const {
    isAuthenticated,
    activeProfile,
    user,
    isLoading: authLoading,
  } = useAuth();
  const ageRange = useSelector((state) => state.Auth.ageRange);
  const sessionToken = useSelector((state) => state.Auth.userToken);
  const canFetch = Boolean(enabled && slug && !authLoading);
  // An opaque request identity also hides old cards during the render before
  // effect cleanup runs on a route, profile, age or session change.
  const request = useMemo(
    () => ({}),
    [
      slug,
      limit,
      canFetch,
      isAuthenticated,
      activeProfile?._id,
      activeProfile?.profileId,
      user?._id,
      user?.mobileNumber,
      ageRange,
      sessionToken,
    ]
  );
  const [state, setState] = useState({ status: "idle" });

  useEffect(() => {
    if (!canFetch) return;
    const controller = new AbortController();
    let retryTimer;
    setState({ request, status: "loading" });

    const load = async (attempt = 0) => {
      try {
        const data = await getContentSuggestions({
          slug,
          limit,
          signal: controller.signal,
        });
        if (!controller.signal.aborted)
          setState({ request, status: "success", data });
      } catch (error) {
        if (controller.signal.aborted) return;
        const status = error?.response?.status;
        const transient = !status || status === 429 || status >= 500;
        if (attempt === 0 && transient) {
          retryTimer = setTimeout(() => load(1), 1000);
          return;
        }
        if (status === 422)
          console.error("Invalid content suggestions query", { slug, limit });
        setState({ request, status: "error", error });
      }
    };
    load();

    return () => {
      controller.abort();
      clearTimeout(retryTimer);
    };
  }, [request, canFetch, slug, limit]);

  const current = canFetch && state.request === request;
  return {
    status: current ? state.status : canFetch ? "loading" : "idle",
    data: current && state.status === "success" ? state.data : undefined,
    error: current ? state.error : undefined,
    isLoading: Boolean(
      enabled && slug && (authLoading || !current || state.status === "loading")
    ),
    isAuthenticated: Boolean(isAuthenticated),
  };
}
